import { DeleteOutlined, DesktopOutlined } from '@ant-design/icons';
import type { MessageDescriptor } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Plural, Trans } from '@lingui/react/macro';
import { Input, Modal } from 'antd';
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { errorMessage } from '../i18n/errorMessages';
import type { RemoteNode } from '../lib/api';
import {
  ApiError,
  bindRepositoryMapping,
  deleteAgent,
  deleteRemoteNode,
  fetchRemoteNodeHostedAgents,
  fetchRemoteNodeStatus,
  fetchRepositoryMappings,
  removeRepositoryMapping,
  resolveRepositoryMapping,
  rotateRemoteNodeConnectionToken,
  verifyRepositoryMapping,
  type RemoteNodeHostedAgent,
  type RemoteNodeStatus,
  type RepositoryMapping,
} from '../lib/api';
import { copyToClipboard } from '../lib/clipboard';
import { getBorgeeWsBaseUrl } from '../lib/runtime-base-url';
import { useAgentStore } from '../stores';
import { AGENT_PROVIDER_LABELS } from './AgentConnectResult';
import { DecommissionModalSurface } from './DecommissionAgentModal';
import PresenceDot from './PresenceDot';
import { RemoteTree } from './RemoteTree';

const STATUS_RETRY_DELAY_MS = 1500;
const STATUS_RETRY_LIMIT = 10;
const buildInstallCommand = (server: string, token: string) =>
  `npx @borgee/remote-helper install --server ${server} --token ${token} --dirs '/path/to/dir'`;
const CHECKLIST_DONE_BADGE: CSSProperties = {
  width: 18,
  height: 18,
  borderRadius: '50%',
  background: 'var(--color-success, #22c55e)',
  color: '#fff',
  fontSize: 11,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginTop: 1,
};
const CHECKLIST_ACTIVE_BADGE: CSSProperties = {
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: '2px solid var(--accent-primary, #5865f2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginTop: 1,
  fontSize: 11,
  color: 'var(--accent-primary, #5865f2)',
};
const CHECKLIST_FUTURE_BADGE: CSSProperties = {
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: '2px solid var(--text-tertiary, #ccc)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginTop: 1,
  fontSize: 11,
  color: 'var(--text-tertiary, #999)',
};

function repositoryMappingErrorMessage(
  error: unknown,
  options: {
    action: 'bind' | 'verify' | 'resolve' | 'remove';
    repository: string;
    status: RemoteNodeStatus | null;
  },
) {
  const { action, repository, status } = options;
  const failures = {
    bind: msg`Could not bind this repository.`,
    verify: msg`Could not verify this repository mapping.`,
    resolve: msg`Could not resolve this repository mapping.`,
    remove: msg`Could not remove this repository mapping.`,
  };
  const fallback = failures[action];
  if (!(error instanceof ApiError)) {
    return fallback;
  }
  switch (error.code) {
    case 'invalid_repository':
      return msg`Repository must identify a github.com owner/repository.`;
    case 'path_not_absolute':
      return msg`Use an absolute path on the selected computer, then retry.`;
    case 'path_not_allowed':
      if (status?.exposed_dirs && status.exposed_dirs.length > 0) {
        return msg`The directory must be inside one of this computer's exposed directories: ${status.exposed_dirs.join(', ')}. Restart Remote Helper with --dirs if the checkout lives elsewhere.`;
      }
      return msg`The directory is outside this computer's exposed directories. Restart Remote Helper with --dirs to expose it, then retry.`;
    case 'directory_not_found':
      return msg`The directory was not found on the selected computer. Check the path and retry.`;
    case 'not_git_repository':
      return msg`The directory exists, but it is not a Git repository.`;
    case 'repository_remote_mismatch':
      return msg`The directory is a Git repository, but none of its remotes match ${repository}. Run git remote -v in that checkout and retry.`;
    case 'repository_mapping_not_found':
      return msg`This computer does not have a saved mapping for that repository. Bind the checkout again, then retry.`;
    case 'repository_mapping_unsupported':
      return msg`This connected helper does not support repository mappings. Update Remote Helper, then reconnect.`;
    case 'repository_mapping_failed':
      return msg`Remote Helper could not complete the repository mapping request. Check the helper logs, then retry.`;
    default:
      return fallback;
  }
}

interface Props {
  nodeId: string;
  node: RemoteNode;
  token?: string;
  onDeleted: () => void;
  onTokenRotated?: (token: string) => void;
}

export default function ComputerDetailView({
  nodeId,
  node,
  token,
  onDeleted,
  onTokenRotated,
}: Props) {
  const { i18n } = useLingui();
  // antd's static Modal.confirm renders outside the React tree, so it never
  // reaches our ConfigProvider and falls back to antd's default palette. The
  // hook form renders through the context holder below and picks up the theme.
  const [modal, modalContextHolder] = Modal.useModal();
  const [status, setStatus] = useState<RemoteNodeStatus | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedExposedDir, setSelectedExposedDir] = useState<string | null>(null);
  const [rotatedToken, setRotatedToken] = useState<string | null>(null);
  const [rotateError, setRotateError] = useState<MessageDescriptor | null>(null);
  const [deleteError, setDeleteError] = useState<MessageDescriptor | null>(null);
  const [hostedAgents, setHostedAgents] = useState<RemoteNodeHostedAgent[]>([]);
  const [hostedAgentsLoading, setHostedAgentsLoading] = useState(true);
  const [hostedAgentsError, setHostedAgentsError] = useState<MessageDescriptor | null>(null);
  const [deletingHostedAgentId, setDeletingHostedAgentId] = useState<string | null>(null);
  const [deleteChecklistOpen, setDeleteChecklistOpen] = useState(false);
  const [deleteImpactAcknowledged, setDeleteImpactAcknowledged] = useState(false);
  const [deleteHostedAcknowledged, setDeleteHostedAcknowledged] = useState(false);
  const [deletingComputer, setDeletingComputer] = useState(false);
  const [repositoryMappings, setRepositoryMappings] = useState<RepositoryMapping[]>([]);
  const [repositoryMappingsLoading, setRepositoryMappingsLoading] = useState(false);
  const [repositoryMappingError, setRepositoryMappingError] = useState<MessageDescriptor | null>(
    null,
  );
  const [repositoryInput, setRepositoryInput] = useState('');
  const [repositoryPathInput, setRepositoryPathInput] = useState('');
  const [repositoryMappingAction, setRepositoryMappingAction] = useState<string | null>(null);
  const [repositoryMappingResult, setRepositoryMappingResult] = useState<MessageDescriptor | null>(
    null,
  );
  const currentNodeIdRef = useRef(nodeId);
  const repositoryMappingLoadGenerationRef = useRef(0);
  const loadAgents = useAgentStore((s) => s.loadAgents);

  useLayoutEffect(() => {
    currentNodeIdRef.current = nodeId;
    repositoryMappingLoadGenerationRef.current += 1;
  }, [nodeId]);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | null = null;
    let attempts = 0;

    setStatus(null);

    const loadStatus = () => {
      fetchRemoteNodeStatus(nodeId)
        .then((nextStatus) => {
          if (cancelled) return;
          setStatus(nextStatus);
          if (
            (nextStatus.online && nextStatus.exposed_dirs !== null) ||
            attempts >= STATUS_RETRY_LIMIT
          ) {
            return;
          }
          attempts += 1;
          timeoutId = window.setTimeout(loadStatus, STATUS_RETRY_DELAY_MS);
        })
        .catch(() => {
          if (cancelled) return;
          setStatus({ online: false, exposed_dirs: null });
          if (attempts >= STATUS_RETRY_LIMIT) {
            return;
          }
          attempts += 1;
          timeoutId = window.setTimeout(loadStatus, STATUS_RETRY_DELAY_MS);
        });
    };

    loadStatus();

    return () => {
      cancelled = true;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [nodeId]);

  const repositoryMappingSupported =
    status?.online === true && status.capabilities?.includes('repository_mapping') === true;

  const refreshRepositoryMappings = async () => {
    const requestNodeId = nodeId;
    const requestGeneration = ++repositoryMappingLoadGenerationRef.current;
    setRepositoryMappingsLoading(true);
    setRepositoryMappingError(null);
    try {
      const mappings = await fetchRepositoryMappings(requestNodeId);
      if (
        currentNodeIdRef.current !== requestNodeId ||
        repositoryMappingLoadGenerationRef.current !== requestGeneration
      ) {
        return;
      }
      setRepositoryMappings(mappings);
    } catch (error) {
      if (
        currentNodeIdRef.current !== requestNodeId ||
        repositoryMappingLoadGenerationRef.current !== requestGeneration
      ) {
        return;
      }
      setRepositoryMappingError(errorMessage(error, msg`Could not load repository mappings.`));
    } finally {
      if (
        currentNodeIdRef.current === requestNodeId &&
        repositoryMappingLoadGenerationRef.current === requestGeneration
      ) {
        setRepositoryMappingsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (repositoryMappingSupported) {
      void refreshRepositoryMappings();
    } else {
      repositoryMappingLoadGenerationRef.current += 1;
      setRepositoryMappings([]);
      setRepositoryMappingsLoading(false);
      setRepositoryMappingError(null);
    }
  }, [nodeId, repositoryMappingSupported]);

  const handleBindRepository = async () => {
    if (!repositoryInput.trim() || !repositoryPathInput.trim() || repositoryMappingAction) return;
    const requestNodeId = nodeId;
    setRepositoryMappingAction('bind');
    setRepositoryMappingError(null);
    setRepositoryMappingResult(null);
    try {
      const mapping = await bindRepositoryMapping(
        requestNodeId,
        repositoryInput.trim(),
        repositoryPathInput.trim(),
      );
      if (currentNodeIdRef.current !== requestNodeId) return;
      setRepositoryInput(mapping.repository);
      setRepositoryPathInput(mapping.path);
      setRepositoryMappingResult(msg`Bound ${mapping.repository} to ${mapping.path}.`);
      await refreshRepositoryMappings();
    } catch (error) {
      if (currentNodeIdRef.current !== requestNodeId) return;
      setRepositoryMappingError(
        repositoryMappingErrorMessage(error, {
          action: 'bind',
          repository: repositoryInput.trim(),
          status,
        }),
      );
    } finally {
      if (currentNodeIdRef.current === requestNodeId) {
        setRepositoryMappingAction(null);
      }
    }
  };

  const runRepositoryMappingAction = async (
    action: 'verify' | 'resolve' | 'remove',
    mapping: RepositoryMapping,
  ) => {
    if (repositoryMappingAction) return;
    const requestNodeId = nodeId;
    setRepositoryMappingAction(`${action}:${mapping.repository}`);
    setRepositoryMappingError(null);
    setRepositoryMappingResult(null);
    try {
      if (action === 'remove') {
        await removeRepositoryMapping(requestNodeId, mapping.repository);
        if (currentNodeIdRef.current !== requestNodeId) return;
        setRepositoryMappingResult(
          msg`Removed ${mapping.repository}. Local files were not deleted.`,
        );
        await refreshRepositoryMappings();
      } else {
        const checked =
          action === 'verify'
            ? await verifyRepositoryMapping(requestNodeId, mapping.repository)
            : await resolveRepositoryMapping(requestNodeId, mapping.repository);
        if (currentNodeIdRef.current !== requestNodeId) return;
        setRepositoryMappingResult(
          action === 'verify'
            ? msg`Verified ${checked.repository} at ${checked.path}.`
            : msg`Resolved ${checked.repository} to ${checked.path}.`,
        );
      }
    } catch (error) {
      if (currentNodeIdRef.current !== requestNodeId) return;
      setRepositoryMappingError(
        repositoryMappingErrorMessage(error, {
          action,
          repository: mapping.repository,
          status,
        }),
      );
    } finally {
      if (currentNodeIdRef.current === requestNodeId) {
        setRepositoryMappingAction(null);
      }
    }
  };

  useEffect(() => {
    setShowToken(false);
    setCopied(false);
    setSelectedExposedDir(null);
    setRotatedToken(null);
    setRotateError(null);
    setDeleteError(null);
    setDeleteChecklistOpen(false);
    setDeleteImpactAcknowledged(false);
    setDeleteHostedAcknowledged(false);
    setDeletingComputer(false);
    setRepositoryMappings([]);
    setRepositoryMappingsLoading(false);
    setRepositoryMappingError(null);
    setRepositoryInput('');
    setRepositoryPathInput('');
    setRepositoryMappingAction(null);
    setRepositoryMappingResult(null);
  }, [nodeId]);

  useEffect(() => {
    let cancelled = false;
    setHostedAgentsLoading(true);
    setHostedAgentsError(null);
    fetchRemoteNodeHostedAgents(nodeId)
      .then((agents) => {
        if (cancelled) return;
        setHostedAgents(agents);
        setHostedAgentsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setHostedAgentsLoading(false);
        setHostedAgentsError(errorMessage(err, msg`Could not load hosted agents.`));
      });
    return () => {
      cancelled = true;
    };
  }, [nodeId]);

  const refreshHostedAgents = async () => {
    setHostedAgentsLoading(true);
    setHostedAgentsError(null);
    try {
      setHostedAgents(await fetchRemoteNodeHostedAgents(nodeId));
    } catch (err) {
      setHostedAgentsError(errorMessage(err, msg`Could not load hosted agents.`));
    } finally {
      setHostedAgentsLoading(false);
    }
  };

  const closeDeleteChecklist = () => {
    if (deletingComputer) return;
    setDeleteChecklistOpen(false);
    setDeleteImpactAcknowledged(false);
    setDeleteHostedAcknowledged(false);
  };

  const handleDelete = () => {
    setDeleteError(null);
    setDeleteChecklistOpen(true);
    setDeleteImpactAcknowledged(false);
    setDeleteHostedAcknowledged(false);
  };

  const handleDeleteComputerConfirmed = async () => {
    setDeleteError(null);
    setDeletingComputer(true);
    try {
      await deleteRemoteNode(nodeId);
      await loadAgents();
      setDeleteChecklistOpen(false);
      onDeleted();
    } catch (err) {
      setDeleteError(errorMessage(err, msg`Could not delete this computer.`));
      setDeletingComputer(false);
    }
  };

  const handleRegenerateToken = () => {
    modal.confirm({
      title: <Trans>Regenerate Connection Token</Trans>,
      content: (
        <Trans>
          The current connection token for "{node.machine_name}" stops working immediately, so this
          computer goes offline right away. It stays offline until you re-run the new install
          command on it.
        </Trans>
      ),
      okText: <Trans>Regenerate Token</Trans>,
      okButtonProps: { danger: true },
      onOk: async () => {
        setRotateError(null);
        let nextToken: string;
        try {
          nextToken = await rotateRemoteNodeConnectionToken(nodeId);
        } catch (err) {
          setRotateError(errorMessage(err, msg`Could not regenerate the connection token.`));
          return;
        }
        setRotatedToken(nextToken);
        // The plaintext token never comes back from any read, so reveal it
        // straight away rather than hiding it behind another click.
        setShowToken(true);
        onTokenRotated?.(nextToken);
      },
    });
  };

  const agentWsServer = getBorgeeWsBaseUrl();
  const currentToken = rotatedToken ?? token;
  // A command built around anything but the real token cannot enroll the
  // machine, so there is no install command to offer until one is in memory.
  const installCommand = currentToken
    ? {
        displayed: buildInstallCommand(agentWsServer, showToken ? currentToken : '••••••••'),
        copyable: buildInstallCommand(agentWsServer, currentToken),
      }
    : null;

  const handleCopy = (command: string) => {
    copyToClipboard(command);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDeleteHostedAgent = (agent: RemoteNodeHostedAgent) => {
    modal.confirm({
      title: <Trans>Delete Hosted Agent</Trans>,
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={{ margin: 0 }}>
            <Trans>
              Delete hosted agent <b>{agent.display_name}</b>?
            </Trans>
          </p>
          <p style={{ margin: 0 }}>
            <Trans>
              This will permanently delete the agent and stop <b>{node.machine_name}</b> from
              hosting it on the next remote-helper sync.
            </Trans>
          </p>
          <p style={{ margin: 0 }}>
            <Trans>
              This will not delete this computer or affect any other hosted agents on it.
            </Trans>
          </p>
        </div>
      ),
      okText: <Trans>Delete</Trans>,
      okButtonProps: { danger: true },
      onOk: async () => {
        setHostedAgentsError(null);
        setDeletingHostedAgentId(agent.id);
        try {
          await deleteAgent(agent.id);
          loadAgents();
          await refreshHostedAgents();
        } catch (err) {
          setHostedAgentsError(errorMessage(err, msg`Could not delete this hosted agent.`));
        } finally {
          setDeletingHostedAgentId(null);
        }
      },
    });
  };

  return (
    <div className="agent-detail-view">
      {modalContextHolder}
      {deleteChecklistOpen && (
        <DeleteComputerChecklistModal
          machineName={node.machine_name}
          hostedAgents={hostedAgents}
          hostedAgentsLoading={hostedAgentsLoading}
          hostedAgentsError={hostedAgentsError}
          deleteError={deleteError}
          impactAcknowledged={deleteImpactAcknowledged}
          hostedAcknowledged={deleteHostedAcknowledged}
          deleting={deletingComputer}
          onAcknowledgeImpact={() => setDeleteImpactAcknowledged(true)}
          onAcknowledgeHosted={() => setDeleteHostedAcknowledged(true)}
          onClose={closeDeleteChecklist}
          onDelete={handleDeleteComputerConfirmed}
        />
      )}
      <div className="agent-detail-header">
        <DesktopOutlined
          aria-hidden="true"
          style={{ fontSize: 20, color: 'var(--text-secondary)' }}
        />
        <div className="agent-detail-header-text">
          <div className="agent-detail-header-name">
            {node.machine_name}
            {status !== null && (
              <PresenceDot
                state={status.online ? 'online' : 'offline'}
                reason={undefined}
                compact
              />
            )}
          </div>
        </div>
      </div>

      <div className="agent-detail-content">
        <div className="agent-settings-scroll">
          <div className="agent-page">
            <div className="agent-profile-hero">
              <span
                className="agent-profile-avatar"
                style={{ background: 'var(--text-secondary)' }}
              >
                <DesktopOutlined aria-hidden="true" style={{ fontSize: 24 }} />
              </span>
              <div className="agent-profile-info">
                <div className="agent-profile-name">
                  {node.machine_name}
                  {status !== null && (
                    <>
                      <PresenceDot
                        state={status.online ? 'online' : 'offline'}
                        reason={undefined}
                        compact
                      />
                      <span className="agent-profile-status">
                        {status.online ? t(i18n)`Online` : t(i18n)`Offline`}
                      </span>
                    </>
                  )}
                </div>
                <div className="agent-profile-slug">
                  <Trans>ID: {node.id}</Trans>
                </div>
              </div>
            </div>

            <div className="agent-profile-field">
              <div className="agent-profile-field-label">
                <Trans>MACHINE NAME</Trans>
              </div>
              <div className="agent-profile-field-value">{node.machine_name}</div>
            </div>

            <div className="agent-profile-field">
              <div className="agent-profile-field-label">
                <Trans>CREATED</Trans>
              </div>
              <div className="agent-profile-field-value">
                {new Date(node.created_at).toLocaleDateString(i18n.locale)}
              </div>
            </div>

            <div className="agent-profile-field">
              <div className="agent-profile-field-label">
                <Trans>LAST SEEN</Trans>
              </div>
              <div className="agent-profile-field-value agent-profile-field-dim">
                {node.last_seen_at
                  ? new Date(node.last_seen_at).toLocaleString(i18n.locale)
                  : t(i18n)`Never`}
              </div>
            </div>

            <div className="agent-profile-field">
              <div className="agent-profile-field-label">
                <Trans>CONNECTION</Trans>
              </div>
              <div className="agent-profile-field-value" style={{ display: 'grid', gap: 12 }}>
                <div>
                  <div
                    className="node-token-area"
                    style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
                  >
                    {currentToken && (
                      <button className="btn btn-sm" onClick={() => setShowToken(!showToken)}>
                        {showToken ? t(i18n)`Hide Token` : t(i18n)`Show Token`}
                      </button>
                    )}
                    <button className="btn btn-sm" onClick={handleRegenerateToken}>
                      <Trans>Regenerate Token</Trans>
                    </button>
                    {currentToken && showToken && (
                      <code className="node-token">{currentToken}</code>
                    )}
                  </div>
                  {!currentToken && (
                    <p
                      className="node-token-unavailable agent-profile-field-dim"
                      style={{ margin: '8px 0 0 0' }}
                    >
                      <Trans>
                        A connection token can be read only at the moment it is issued, so there is
                        no install command to show for this computer. Regenerate the token to get a
                        working one — the current token stops authenticating immediately, and this
                        computer stays offline until you run the new install command on it.
                      </Trans>
                    </p>
                  )}
                  {rotateError && (
                    <p className="node-token-error remote-error" style={{ margin: '8px 0 0 0' }}>
                      {i18n._(rotateError)}
                    </p>
                  )}
                </div>
                {installCommand && (
                  <div>
                    <div className="agent-profile-field-label" style={{ marginBottom: 6 }}>
                      <Trans>INSTALL COMMAND</Trans>
                    </div>
                    <div
                      className="node-cmd-box"
                      style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
                    >
                      <code>{installCommand.displayed}</code>
                      <button
                        className="btn btn-sm"
                        onClick={() => handleCopy(installCommand.copyable)}
                      >
                        {copied ? t(i18n)`Copied` : t(i18n)`Copy`}
                      </button>
                    </div>
                    <p className="agent-profile-field-dim" style={{ margin: '8px 0 0 0' }}>
                      <Trans>
                        Run this on the target computer (macOS or Linux). Replace the quoted{' '}
                        <code>'/path/to/dir'</code> value with at least one directory to expose.
                      </Trans>
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="agent-profile-field">
              <div className="agent-profile-field-label">
                <Trans>EXPOSED DIRECTORIES</Trans>
              </div>
              <div className="agent-profile-field-value" style={{ display: 'grid', gap: 8 }}>
                {status === null ? (
                  <div className="agent-profile-field-dim">
                    <Trans>Loading...</Trans>
                  </div>
                ) : status.online && status.exposed_dirs && status.exposed_dirs.length > 0 ? (
                  <>
                    {selectedExposedDir ? (
                      <div className="remote-panel" style={{ marginTop: 4 }}>
                        <div className="remote-panel-header">
                          <button
                            className="remote-back-btn"
                            onClick={() => setSelectedExposedDir(null)}
                          >
                            <Trans>← Back</Trans>
                          </button>
                          <span className="remote-panel-title">{selectedExposedDir}</span>
                        </div>
                        <RemoteTree nodeId={nodeId} rootPath={selectedExposedDir} />
                      </div>
                    ) : (
                      <div className="remote-binding-list">
                        {status.exposed_dirs.map((dir) => (
                          <button
                            key={dir}
                            type="button"
                            className="remote-binding-item"
                            onClick={() => setSelectedExposedDir(dir)}
                            style={{ width: '100%', textAlign: 'left', background: 'none' }}
                          >
                            <span className="remote-binding-icon">🖥️</span>
                            <div className="remote-binding-info">
                              <div className="remote-binding-name">{dir}</div>
                              <div className="remote-binding-meta">
                                <Trans>Browse this computer root</Trans>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : status.online ? (
                  <div className="agent-profile-field-dim">
                    <Trans>
                      Connected, but this computer has not announced any exposed directories yet.
                    </Trans>
                  </div>
                ) : (
                  <div className="agent-profile-field-dim">
                    <Trans>
                      Exposed directories are available only while this computer is online.
                    </Trans>
                  </div>
                )}
              </div>
            </div>

            <div className="agent-profile-field">
              <div className="agent-profile-field-label">
                <Trans>REPOSITORY MAPPINGS</Trans>
              </div>
              <div className="agent-profile-field-value" style={{ display: 'grid', gap: 10 }}>
                {status === null ? (
                  <div className="agent-profile-field-dim">
                    <Trans>Loading...</Trans>
                  </div>
                ) : !status.online ? (
                  <div className="agent-profile-field-dim">
                    <Trans>
                      Start Remote Helper on this computer to manage repository mappings.
                    </Trans>
                  </div>
                ) : !repositoryMappingSupported ? (
                  <div className="agent-profile-field-dim">
                    <Trans>
                      This connected helper does not support repository mappings. Update Remote
                      Helper, then reconnect.
                    </Trans>
                  </div>
                ) : (
                  <>
                    <div className="agent-profile-field-dim">
                      <Trans>
                        Bind an existing local Git checkout. Borgee validates its remotes but never
                        clones, pulls, runs Git, or deletes local code.
                      </Trans>
                    </div>
                    <Input
                      aria-label={t(i18n)`Repository identity`}
                      value={repositoryInput}
                      onChange={(event) => setRepositoryInput(event.target.value)}
                      placeholder="github.com/owner/repository"
                    />
                    <Input
                      aria-label={t(i18n)`Existing repository directory`}
                      value={repositoryPathInput}
                      onChange={(event) => setRepositoryPathInput(event.target.value)}
                      placeholder="/absolute/path/to/existing/checkout"
                    />
                    {status.exposed_dirs && status.exposed_dirs.length > 0 && (
                      <div className="agent-profile-field-dim">
                        <Trans>
                          The checkout must live under one of this computer's exposed directories:{' '}
                          {status.exposed_dirs.join(', ')}.
                        </Trans>
                      </div>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={
                        !repositoryInput.trim() ||
                        !repositoryPathInput.trim() ||
                        repositoryMappingAction !== null
                      }
                      onClick={() => void handleBindRepository()}
                    >
                      {repositoryMappingAction === 'bind'
                        ? t(i18n)`Binding...`
                        : t(i18n)`Add Repository Mapping`}
                    </button>
                    {repositoryMappingsLoading ? (
                      <div className="agent-profile-field-dim">
                        <Trans>Loading mappings...</Trans>
                      </div>
                    ) : repositoryMappings.length === 0 ? (
                      <div className="agent-profile-field-dim">
                        <Trans>No repository mappings yet.</Trans>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gap: 8 }}>
                        {repositoryMappings.map((mapping) => (
                          <div
                            key={mapping.repository}
                            style={{
                              border: '1px solid var(--border-color, #e5e7eb)',
                              borderRadius: 8,
                              padding: 10,
                              display: 'grid',
                              gap: 6,
                            }}
                          >
                            <code>{mapping.repository}</code>
                            <span className="agent-profile-field-dim">{mapping.path}</span>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                className="btn btn-sm"
                                disabled={repositoryMappingAction !== null}
                                onClick={() => void runRepositoryMappingAction('verify', mapping)}
                              >
                                <Trans>Verify</Trans>
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm"
                                disabled={repositoryMappingAction !== null}
                                onClick={() => void runRepositoryMappingAction('resolve', mapping)}
                              >
                                <Trans>Resolve</Trans>
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm"
                                disabled={repositoryMappingAction !== null}
                                onClick={() => void runRepositoryMappingAction('remove', mapping)}
                              >
                                <Trans>Remove Mapping</Trans>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {repositoryMappingResult && (
                      <div
                        role="status"
                        style={{ color: 'var(--color-success, #22c55e)', fontSize: 12 }}
                      >
                        {i18n._(repositoryMappingResult)}
                      </div>
                    )}
                    {repositoryMappingError && (
                      <div role="alert" className="remote-error">
                        {i18n._(repositoryMappingError)}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="agent-profile-field">
              <div className="agent-profile-field-label">
                <Trans>HOSTED AGENTS ({hostedAgents.length})</Trans>
              </div>
              <div className="agent-profile-field-value" style={{ display: 'grid', gap: 10 }}>
                <div className="agent-profile-field-dim">
                  <Plural
                    value={hostedAgents.length}
                    one="This computer currently hosts # agent."
                    other="This computer currently hosts # agents."
                  />
                </div>

                {hostedAgentsLoading ? (
                  <div className="agent-profile-field-dim">
                    <Trans>Loading...</Trans>
                  </div>
                ) : hostedAgentsError ? (
                  <p className="remote-error" style={{ margin: 0 }}>
                    {i18n._(hostedAgentsError)}
                  </p>
                ) : hostedAgents.length === 0 ? (
                  <div className="agent-profile-field-dim">
                    <Trans>No hosted agents yet.</Trans>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {hostedAgents.map((agent) => (
                      <div
                        key={agent.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 12,
                          alignItems: 'center',
                          padding: 12,
                          border: '1px solid var(--border-color, #e5e7eb)',
                          borderRadius: 8,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600 }}>{agent.display_name}</div>
                          <div className="agent-profile-field-dim">
                            {AGENT_PROVIDER_LABELS[agent.provider]}
                            {agent.enabled ? t(i18n)` · Enabled` : t(i18n)` · Stopped`}
                            {agent.disabled ? t(i18n)` · Agent disabled` : ''}
                          </div>
                          <div
                            className="agent-profile-field-dim"
                            style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}
                          >
                            <Trans>Agent ID: {agent.id}</Trans>
                          </div>
                        </div>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteHostedAgent(agent)}
                          disabled={deletingHostedAgentId === agent.id}
                        >
                          {deletingHostedAgentId === agent.id
                            ? t(i18n)`Deleting...`
                            : t(i18n)`Delete Agent`}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="agent-profile-field">
              <div className="agent-profile-field-label">
                <Trans>ACTIONS</Trans>
              </div>
              <div style={{ marginTop: 4 }}>
                <button className="btn btn-sm btn-danger" onClick={handleDelete}>
                  <DeleteOutlined aria-hidden="true" /> <Trans>Delete Computer</Trans>{' '}
                </button>
                {deleteError && (
                  <p className="node-delete-error remote-error" style={{ margin: '8px 0 0 0' }}>
                    {i18n._(deleteError)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DeleteComputerChecklistModal({
  machineName,
  hostedAgents,
  hostedAgentsLoading,
  hostedAgentsError,
  deleteError,
  impactAcknowledged,
  hostedAcknowledged,
  deleting,
  onAcknowledgeImpact,
  onAcknowledgeHosted,
  onClose,
  onDelete,
}: {
  machineName: string;
  hostedAgents: RemoteNodeHostedAgent[];
  hostedAgentsLoading: boolean;
  hostedAgentsError: MessageDescriptor | null;
  deleteError: MessageDescriptor | null;
  impactAcknowledged: boolean;
  hostedAcknowledged: boolean;
  deleting: boolean;
  onAcknowledgeImpact: () => void;
  onAcknowledgeHosted: () => void;
  onClose: () => void;
  onDelete: () => void;
}) {
  const { i18n } = useLingui();
  const readyToDelete = impactAcknowledged && hostedAcknowledged;

  return (
    <DecommissionModalSurface maxWidth={520} busy={deleting} onClose={onClose}>
      <h3>
        <Trans>Delete {machineName}</Trans>
      </h3>
      <p style={{ fontSize: 13, margin: '16px 0', color: 'var(--text-secondary)' }}>
        <Trans>
          This checklist confirms what will be removed before this computer is deleted for good.
        </Trans>
      </p>
      {deleteError && (
        <p className="node-delete-error remote-error" style={{ margin: '0 0 12px 0' }}>
          {i18n._(deleteError)}
        </p>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          marginTop: 16,
          marginBottom: 8,
        }}
      >
        {impactAcknowledged ? (
          <>
            <span style={CHECKLIST_DONE_BADGE}>&#10003;</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-success, #22c55e)' }}>
              <Trans>Connection cleanup acknowledged</Trans>
            </span>
          </>
        ) : (
          <>
            <span style={CHECKLIST_ACTIVE_BADGE}>1</span>
            <span style={{ fontSize: 13, fontWeight: 500 }}>
              <Trans>Review connection impact</Trans>
            </span>
            <button
              className="btn btn-sm btn-primary"
              style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 10px' }}
              onClick={onAcknowledgeImpact}
            >
              <Trans>Acknowledge</Trans>
            </button>
          </>
        )}
      </div>
      {!impactAcknowledged && (
        <div style={{ marginLeft: 28 }}>
          <p style={{ fontSize: 12, marginBottom: 0, color: 'var(--text-secondary)' }}>
            <Trans>
              Deleting this computer removes its connection token, drops its bindings, and
              disconnects it from Borgee immediately.
            </Trans>
          </p>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          marginTop: 12,
          marginBottom: 8,
        }}
      >
        {hostedAcknowledged ? (
          <>
            <span style={CHECKLIST_DONE_BADGE}>&#10003;</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-success, #22c55e)' }}>
              <Trans>Hosted-agent cascade acknowledged</Trans>
            </span>
          </>
        ) : impactAcknowledged ? (
          <>
            <span style={CHECKLIST_ACTIVE_BADGE}>2</span>
            <span style={{ fontSize: 13, fontWeight: 500 }}>
              <Trans>Review hosted-agent impact</Trans>
            </span>
            <button
              className="btn btn-sm btn-primary"
              style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 10px' }}
              onClick={onAcknowledgeHosted}
            >
              <Trans>Acknowledge</Trans>
            </button>
          </>
        ) : (
          <>
            <span style={CHECKLIST_FUTURE_BADGE}>2</span>
            <span style={{ fontSize: 13, color: 'var(--text-tertiary, #999)' }}>
              <Trans>Review hosted-agent impact</Trans>
            </span>
          </>
        )}
      </div>
      {impactAcknowledged && !hostedAcknowledged && (
        <div style={{ marginLeft: 28 }}>
          <p style={{ fontSize: 12, marginBottom: 8, color: 'var(--text-secondary)' }}>
            <Trans>Every hosted agent on this computer will be deleted too.</Trans>
          </p>
          {hostedAgentsLoading ? (
            <p className="agent-profile-field-dim" style={{ margin: 0 }}>
              <Trans>Loading hosted agents...</Trans>
            </p>
          ) : hostedAgentsError ? (
            <p className="remote-error" style={{ margin: 0 }}>
              <Trans>
                {i18n._(hostedAgentsError)} Hosted agents, if any, are still deleted together with
                this computer.
              </Trans>
            </p>
          ) : hostedAgents.length === 0 ? (
            <p className="agent-profile-field-dim" style={{ margin: 0 }}>
              <Trans>No hosted agents are currently bound to this computer.</Trans>
            </p>
          ) : (
            <div style={{ display: 'grid', gap: 4, marginBottom: 8 }}>
              {hostedAgents.map((agent) => (
                <div key={agent.id} style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  <b>{agent.display_name}</b> · {AGENT_PROVIDER_LABELS[agent.provider]} · {agent.id}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          marginTop: 12,
          marginBottom: 16,
        }}
      >
        {readyToDelete ? (
          <>
            <span style={CHECKLIST_ACTIVE_BADGE}>3</span>
            <span style={{ fontSize: 13, fontWeight: 500 }}>
              <Trans>Delete permanently</Trans>
            </span>
          </>
        ) : (
          <>
            <span style={CHECKLIST_FUTURE_BADGE}>3</span>
            <span style={{ fontSize: 13, color: 'var(--text-tertiary, #999)' }}>
              <Trans>Delete permanently</Trans>
            </span>
          </>
        )}
      </div>

      <p style={{ fontSize: 12, marginTop: 0, marginBottom: 16, color: 'var(--text-secondary)' }}>
        <Trans>This action cannot be undone.</Trans>
      </p>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn btn-sm" onClick={onClose} disabled={deleting}>
          <Trans>Cancel</Trans>
        </button>
        <button
          className="btn btn-sm"
          style={{
            background: 'var(--text-danger, #d32f2f)',
            color: '#fff',
            borderColor: 'var(--text-danger, #d32f2f)',
            opacity: readyToDelete ? 1 : 0.65,
          }}
          onClick={onDelete}
          disabled={!readyToDelete || deleting}
        >
          {deleting ? t(i18n)`Deleting...` : t(i18n)`Delete Computer`}
        </button>
      </div>
    </DecommissionModalSurface>
  );
}
